/**
 * MirrorInterestsPicker — Sélecteur d'intérêts + zones géographiques du Miroir.
 *
 * V23.15.63 — Composant réutilisable (contenu-seul, pas de shell modal).
 * Utilisé dans :
 *   • WelcomeModal (première configuration, one-shot)
 *   • MiroirDuMatin PreferencesModal (modification ultérieure via engrenage)
 *
 * Props :
 *   value    : { world_enabled, ecosystem_enabled, interests[], geo_continents[], geo_countries[], geo_regions[], configured }
 *   onChange : (nextValue) => void
 *   mode     : "onboarding" (met en avant "Sauter" / "Configurer") | "settings"
 *
 * Zéro nouvelle dépendance.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Globe, Compass, Sparkles, X, Plus, Loader2, Download } from "lucide-react";

const INTERESTS = [
  { slug: "regulation_normes",   label: "Régulation, normes & standards" },
  { slug: "geopolitique",        label: "Géopolitique" },
  { slug: "banques_centrales",   label: "Banques centrales" },
  { slug: "ma_strategiques",     label: "M&A stratégiques" },
  { slug: "marches_financiers",  label: "Marchés financiers" },
  { slug: "private_equity_vc",   label: "Private Equity & VC" },
  { slug: "immobilier",          label: "Immobilier" },
  { slug: "crypto_tokenisation", label: "Crypto & tokenisation" },
  { slug: "ia_modeles",          label: "IA & modèles" },
  { slug: "cybersecurite",       label: "Cybersécurité" },
  { slug: "deep_tech",           label: "Deep tech" },
  { slug: "agroalimentaire",     label: "Agroalimentaire" },
  { slug: "pharma_medical",      label: "Pharma & médical" },
  { slug: "sante_biotech",       label: "Santé & biotech" },
  { slug: "energie_climat",      label: "Énergie & climat" },
  { slug: "defense_armement",    label: "Défense & armement" },
  { slug: "financement",         label: "Financement & dette privée" },
  { slug: "afrique",             label: "Afrique — économie & deals" },
];

const CONTINENTS = [
  { slug: "europe",          label: "Europe" },
  { slug: "amerique_nord",   label: "Amérique du Nord" },
  { slug: "amerique_latine", label: "Amérique latine" },
  { slug: "afrique",         label: "Afrique" },
  { slug: "asie_pacifique",  label: "Asie-Pacifique" },
  { slug: "mena",            label: "MENA" },
  { slug: "oceanie",         label: "Océanie" },
];

// Liste courte de pays fréquents pour un dirigeant CH/FR/AF/US (le champ accepte n'importe quel texte)
const COUNTRIES_SUGGESTIONS = [
  "Suisse", "France", "Belgique", "Luxembourg", "Allemagne", "Royaume-Uni",
  "Italie", "Espagne", "Pays-Bas", "Portugal", "Irlande",
  "États-Unis", "Canada", "Brésil", "Mexique", "Argentine",
  "Côte d'Ivoire", "Sénégal", "Nigeria", "Maroc", "Tunisie", "Kenya", "Afrique du Sud",
  "EAU", "Arabie saoudite", "Qatar", "Israël", "Turquie",
  "Chine", "Japon", "Corée du Sud", "Singapour", "Hong Kong", "Inde", "Australie",
];

function ToggleTile({ active, onClick, label, testid, accent = "#EF9F27" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left text-[12px] px-3 py-2 rounded border transition ${
        active
          ? "border-transparent text-black font-semibold"
          : "border-white/10 text-zinc-300 hover:border-white/25"
      }`}
      style={active ? { background: accent } : { background: "rgba(255,255,255,0.02)" }}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

export default function MirrorInterestsPicker({ value, onChange, mode = "settings" }) {
  const v = value || {};
  const [customCountry, setCustomCountry] = useState("");
  const [customRegion, setCustomRegion] = useState("");
  const [importing, setImporting] = useState(false);

  const setField = (k, next) => onChange({ ...v, [k]: next });
  const toggleIn = (k, item) => {
    const cur = new Set(v[k] || []);
    if (cur.has(item)) cur.delete(item); else cur.add(item);
    setField(k, Array.from(cur));
  };

  const importFromPositionnement = async () => {
    setImporting(true);
    try {
      const r = await api.get("/workspace/mirror/positionnement-hint");
      const hint = r.data || {};
      const nextValue = { ...v };
      if (Array.isArray(hint.continents) && hint.continents.length) {
        const merged = new Set([...(v.geo_continents || []), ...hint.continents]);
        nextValue.geo_continents = Array.from(merged);
      }
      if (Array.isArray(hint.countries) && hint.countries.length) {
        const merged = new Set([...(v.geo_countries || []), ...hint.countries]);
        nextValue.geo_countries = Array.from(merged);
      }
      if (Array.isArray(hint.regions) && hint.regions.length) {
        const merged = new Set([...(v.geo_regions || []), ...hint.regions]);
        nextValue.geo_regions = Array.from(merged);
      }
      onChange(nextValue);
    } catch {
      // silent — pas grave si absent
    } finally {
      setImporting(false);
    }
  };

  const addCountry = (label) => {
    if (!label || !label.trim()) return;
    const clean = label.trim();
    if ((v.geo_countries || []).includes(clean)) return;
    setField("geo_countries", [...(v.geo_countries || []), clean]);
    setCustomCountry("");
  };

  const addRegion = (label) => {
    if (!label || !label.trim()) return;
    const clean = label.trim();
    if ((v.geo_regions || []).includes(clean)) return;
    setField("geo_regions", [...(v.geo_regions || []), clean]);
    setCustomRegion("");
  };

  return (
    <div className="space-y-5" data-testid="mirror-interests-picker">
      {mode === "onboarding" && (
        <div className="text-[12px] text-zinc-300 leading-relaxed">
          Pour que <span className="text-white font-semibold">News</span> vous serve
          des observations utiles dès demain, dites-nous ce que Noam doit regarder pour vous.
          <span className="text-zinc-500"> Aucun champ n&apos;est obligatoire, tout est modifiable plus tard.</span>
        </div>
      )}

      {/* Section 1 — Quels miroirs */}
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Quels miroirs recevoir</div>
        <div className="grid sm:grid-cols-2 gap-2">
          <label className="flex items-start gap-2.5 p-2.5 rounded border border-white/10 hover:border-[#378ADD]/40 cursor-pointer" data-testid="mip-world-row">
            <input
              type="checkbox"
              checked={!!v.world_enabled}
              onChange={(e) => setField("world_enabled", e.target.checked)}
              className="mt-0.5 accent-[#378ADD]"
              data-testid="mip-world-toggle"
            />
            <div className="min-w-0">
              <div className="text-[12px] text-white font-semibold inline-flex items-center gap-1.5">
                <Globe className="w-3 h-3 text-[#378ADD]" /> Le monde a bougé
              </div>
              <p className="text-[10.5px] text-zinc-500 mt-0.5 leading-snug">Journal mondial filtré par vos intérêts.</p>
            </div>
          </label>
          <label className="flex items-start gap-2.5 p-2.5 rounded border border-white/10 hover:border-[#EF9F27]/40 cursor-pointer" data-testid="mip-eco-row">
            <input
              type="checkbox"
              checked={!!v.ecosystem_enabled}
              onChange={(e) => setField("ecosystem_enabled", e.target.checked)}
              className="mt-0.5 accent-[#EF9F27]"
              data-testid="mip-eco-toggle"
            />
            <div className="min-w-0">
              <div className="text-[12px] text-white font-semibold inline-flex items-center gap-1.5">
                <Compass className="w-3 h-3 text-[#EF9F27]" /> Votre monde a bougé
              </div>
              <p className="text-[10.5px] text-zinc-500 mt-0.5 leading-snug">Signaux de votre écosystème personnel.</p>
            </div>
          </label>
        </div>
      </div>

      {/* Section 2 — Centres d'intérêt */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Centres d&apos;intérêt</div>
          <div className="text-[10px] text-zinc-500 tabular-nums" data-testid="mip-interests-count">
            {(v.interests || []).length} sélectionné{(v.interests || []).length > 1 ? "s" : ""}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5" data-testid="mip-interests-grid">
          {INTERESTS.map((it) => (
            <ToggleTile
              key={it.slug}
              label={it.label}
              active={(v.interests || []).includes(it.slug)}
              onClick={() => toggleIn("interests", it.slug)}
              accent="#EF9F27"
              testid={`mip-interest-${it.slug}`}
            />
          ))}
        </div>
      </div>

      {/* Section 3 — Zones géographiques */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Zones à surveiller</div>
          <button
            type="button"
            onClick={importFromPositionnement}
            disabled={importing}
            className="text-[10px] text-[#378ADD] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="mip-import-positionnement"
            title="Récupère la zone d'opération renseignée dans votre module Positionnement"
          >
            {importing ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Download className="w-2.5 h-2.5" />}
            Copier ma zone de Positionnement
          </button>
        </div>

        {/* 3a — Continents */}
        <div className="pt-1">
          <div className="text-[10px] text-zinc-500 mb-1">Continents (macro)</div>
          <div className="flex flex-wrap gap-1.5" data-testid="mip-continents">
            {CONTINENTS.map((c) => (
              <ToggleTile
                key={c.slug}
                label={c.label}
                active={(v.geo_continents || []).includes(c.slug)}
                onClick={() => toggleIn("geo_continents", c.slug)}
                accent="#378ADD"
                testid={`mip-continent-${c.slug}`}
              />
            ))}
          </div>
        </div>

        {/* 3b — Pays */}
        <div className="pt-1">
          <div className="text-[10px] text-zinc-500 mb-1">Pays (facultatif — focus)</div>
          <div className="flex flex-wrap gap-1 mb-1.5" data-testid="mip-countries-current">
            {(v.geo_countries || []).map((c) => (
              <span
                key={c}
                className="text-[11px] px-2 py-0.5 rounded-full bg-[#7F77DD]/15 border border-[#7F77DD]/40 text-[#C4BFFF] inline-flex items-center gap-1"
                data-testid={`mip-country-tag-${c}`}
              >
                {c}
                <button
                  type="button"
                  onClick={() => setField("geo_countries", (v.geo_countries || []).filter((x) => x !== c))}
                  className="text-[#C4BFFF]/70 hover:text-white"
                  aria-label={`Retirer ${c}`}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <input
              list="mip-countries-suggestions"
              value={customCountry}
              onChange={(e) => setCustomCountry(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCountry(customCountry); } }}
              placeholder="Ajouter un pays (Enter)…"
              className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white focus:outline-none focus:border-[#378ADD]"
              data-testid="mip-country-input"
            />
            <datalist id="mip-countries-suggestions">
              {COUNTRIES_SUGGESTIONS.map((c) => <option key={c} value={c} />)}
            </datalist>
            <button
              type="button"
              onClick={() => addCountry(customCountry)}
              disabled={!customCountry.trim()}
              className="text-[10px] px-2 py-1 rounded bg-white/[0.06] border border-white/10 text-zinc-300 hover:bg-white/[0.12] disabled:opacity-40"
              data-testid="mip-country-add"
            >
              <Plus className="w-2.5 h-2.5 inline" /> Ajouter
            </button>
          </div>
        </div>

        {/* 3c — Régions */}
        <div className="pt-1">
          <div className="text-[10px] text-zinc-500 mb-1">Focus locaux (facultatif — ex : Suisse romande, Grand Paris, Abidjan)</div>
          <div className="flex flex-wrap gap-1 mb-1.5" data-testid="mip-regions-current">
            {(v.geo_regions || []).map((r) => (
              <span
                key={r}
                className="text-[11px] px-2 py-0.5 rounded-full bg-[#1D9E75]/15 border border-[#1D9E75]/40 text-[#8CE5C0] inline-flex items-center gap-1"
                data-testid={`mip-region-tag-${r}`}
              >
                {r}
                <button
                  type="button"
                  onClick={() => setField("geo_regions", (v.geo_regions || []).filter((x) => x !== r))}
                  className="text-[#8CE5C0]/70 hover:text-white"
                  aria-label={`Retirer ${r}`}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <input
              value={customRegion}
              onChange={(e) => setCustomRegion(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRegion(customRegion); } }}
              placeholder="Ajouter un focus régional (Enter)…"
              className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white focus:outline-none focus:border-[#1D9E75]"
              data-testid="mip-region-input"
            />
            <button
              type="button"
              onClick={() => addRegion(customRegion)}
              disabled={!customRegion.trim()}
              className="text-[10px] px-2 py-1 rounded bg-white/[0.06] border border-white/10 text-zinc-300 hover:bg-white/[0.12] disabled:opacity-40"
              data-testid="mip-region-add"
            >
              <Plus className="w-2.5 h-2.5 inline" /> Ajouter
            </button>
          </div>
        </div>
      </div>

      <div className="text-[10px] text-zinc-500 italic inline-flex items-center gap-1 pt-1">
        <Sparkles className="w-2.5 h-2.5 text-[#EF9F27]" />
        Vous pourrez modifier ces préférences à tout moment via l&apos;engrenage du Miroir.
      </div>
    </div>
  );
}

/** Charge les préférences (utile pour les modales appelantes) */
export async function loadMirrorPreferences() {
  try {
    const r = await api.get("/workspace/mirror/preferences");
    return r.data;
  } catch {
    return {
      world_enabled: true,
      ecosystem_enabled: true,
      world_geo_scope: "mondial",
      interests: [],
      geo_continents: [],
      geo_countries: [],
      geo_regions: [],
      configured: false,
    };
  }
}

/** Sauvegarde les préférences et retourne le nouveau document */
export async function saveMirrorPreferences(prefs, markConfigured = true) {
  const payload = {
    world_enabled: !!prefs.world_enabled,
    ecosystem_enabled: !!prefs.ecosystem_enabled,
    interests: prefs.interests || [],
    geo_continents: prefs.geo_continents || [],
    geo_countries: prefs.geo_countries || [],
    geo_regions: prefs.geo_regions || [],
    configured: markConfigured ? true : !!prefs.configured,
  };
  const r = await api.post("/workspace/mirror/preferences", payload);
  return r.data;
}

/** Prefs par défaut */
export const DEFAULT_MIRROR_PREFS = {
  world_enabled: true,
  ecosystem_enabled: true,
  interests: [],
  geo_continents: [],
  geo_countries: [],
  geo_regions: [],
  configured: false,
};
